import React, { useState } from "react";
import {
  FaStore,
  FaBuilding,
  FaMicrochip,
  FaWarehouse,
  FaTools,
  FaUserTie,
} from "react-icons/fa";
import { StatCard } from "../../shared";
import usePermission from "../../../Hooks/usePermission";
import OverviewDrawer from "./OverviewDrawer";

/**
 * Bayi dashboard üst durum kutuları.
 *
 * Kapsam (scope) seçicisinden BAĞIMSIZDIR — kutular her zaman bayinin tüm
 * ağacını özetler. Scope yalnızca alttaki tüketim bloklarını filtreler;
 * kutuları da filtreleseydik "Bayim" seçiliyken müşteri sayısı 0 görünürdü.
 *
 * Yalnızca FİRMA SAYISI kutuları yetkiye bağlıdır: "Alt Bayi" -> dealers.read,
 * "Müşteri Firma" -> customers.read (ikisi de firma listesi yetkisi). CİHAZ
 * kutuları bağlı değildir — dashboard'un kendisi zaten dashboard.read istiyor
 * ve bayinin kendi ağındaki cihaz sayısı firma listesi yetkisiyle ilgisiz.
 * (Önce "Müşterideki Cihaz" da customers.read'e bağlıydı; bu yetkisi olmayan
 * bayide cihazların çoğu müşteride olduğu için kutu kaybolup tablo yanıltıcı
 * hale geliyordu.)
 */
export default function OverviewStats({ data, isLoading = false }) {
  const { hasPermission } = usePermission();

  const canSeeDealers = hasPermission("dealers.read");
  const canSeeCustomers = hasPermission("customers.read");
  // Cihaz kutularının listesi devices.read ister — kutu her zaman görünür ama
  // tıklanabilirlik yetkiye bağlı (kullanıcı kararı: "izin varsa tıklanabilsin").
  const canSeeDevices = hasPermission("devices.read");
  const [openCard, setOpenCard] = useState(null);

  const cards = [
    canSeeDealers && {
      key: "subDealers",
      title: "Alt Bayi",
      count: data?.subDealerCount ?? 0,
      icon: <FaStore />,
      color: "indigo",
      drawer: { kind: "firms", relation: "bayi" },
    },
    canSeeCustomers && {
      key: "customers",
      title: "Müşteri Firma",
      count: data?.customerCount ?? 0,
      icon: <FaBuilding />,
      color: "blue",
      drawer: { kind: "firms", relation: "musteri" },
    },
    // Cihaz kırılımı bayi konumuna (dealerDeviceStatusName) göre gelir:
    // Depo / Firma / Servis / Plasiyer. Dördü toplandığında ağaçtaki tüm
    // cihazlar çıkar — sahiplik bazlı sayımda servis ve plasiyerdeki cihazlar
    // "bayideki" görünüyordu.
    {
      key: "dealerDevices",
      drawer: canSeeDevices ? { kind: "devices", status: "Depo" } : null,
      title: "Bayideki Cihaz",
      count: data?.dealerDeviceCount ?? 0,
      icon: <FaWarehouse />,
      color: "amber",
    },
    {
      key: "customerDevices",
      drawer: canSeeDevices ? { kind: "devices", status: "Firma" } : null,
      title: "Müşterideki Cihaz",
      count: data?.customerDeviceCount ?? 0,
      icon: <FaMicrochip />,
      color: "emerald",
    },
    {
      key: "serviceDevices",
      drawer: canSeeDevices ? { kind: "devices", status: "Servis" } : null,
      title: "Servisteki Cihaz",
      count: data?.serviceDeviceCount ?? 0,
      icon: <FaTools />,
      color: "purple",
    },
    {
      key: "playmakerDevices",
      drawer: canSeeDevices ? { kind: "devices", status: "Plasiyer" } : null,
      title: "Plasiyerdeki Cihaz",
      count: data?.playmakerDeviceCount ?? 0,
      icon: <FaUserTie />,
      color: "red",
    },
  ].filter(Boolean);

  if (cards.length === 0) return null;

  // Kartlar sayfa satırına yayılır: kolon sayısı kart sayısına eşit. Sabit
  // grid-cols-6 kullanılsaydı 5 kartlı kullanıcıda sağda boşluk kalırdı.
  // Sınıflar literal — Tailwind JIT tarayabilsin diye eşlemede sabit yazılı.
  const LG_COLS = {
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
  };
  const gridClass = `grid grid-cols-2 sm:grid-cols-3 ${LG_COLS[cards.length] || "lg:grid-cols-4"} gap-2 sm:gap-3 px-2 sm:px-0`;

  if (isLoading) {
    return (
      <div className={gridClass}>
        {cards.map((c) => (
          <div
            key={c.key}
            className="rounded-2xl bg-white border border-gray-200 shadow-sm h-[104px] sm:h-[132px] animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className={gridClass}>
        {cards.map((c) => (
          <StatCard
            key={c.key}
            title={c.title}
            count={c.count}
            icon={c.icon}
            color={c.color}
            // StatCard her zaman cursor-pointer; yetkisi olmayanda tıklama
            // hiçbir şey yapmasın diye onClick verilmez.
            onClick={c.drawer ? () => setOpenCard(c) : undefined}
          />
        ))}
      </div>
      <OverviewDrawer
        open={Boolean(openCard)}
        card={openCard ? { ...openCard.drawer, title: openCard.title } : null}
        onClose={() => setOpenCard(null)}
      />
    </>
  );
}
